import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  FormControlLabel,
  Radio,
  Stack,
  Typography,
} from '@mui/material'
import useCreateEntrataClient from '@/hooks/useCreateEntrataClient'
import { OverlayState } from '@/hooks/useOverlay'
import queryKeys from '@/service-library/query-keys'
import {
  ENTRATA_CLIENT_PRODUCT_LABELS,
  EntrataClientProduct,
} from '@/types/entrata-client'
import { showErrorSnackbar, showSuccessSnackbar } from '@/utils/snackbars'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'
import { FormTextField, PixyDocsForm } from '@/components/forms'
import FormRadioGroup from '@/components/forms/FormRadioGroup'

type NewEntrataClientDialogProps = {
  overlay: OverlayState
}

type FormValues = {
  name: string
  subdomain: string
  cid: string
  product: EntrataClientProduct
}

const defaultValues: FormValues = {
  name: '',
  subdomain: '',
  cid: '',
  product: 'invoice_processing',
}

const requiredRule = {
  validate: (value: string) => value.trim().length > 0,
}

export default function NewEntrataClientDialog({
  overlay,
}: NewEntrataClientDialogProps) {
  const { createEntrataClient, isPending } = useCreateEntrataClient({
    sideEffectQueryKeys: [queryKeys.organizations.lists()],
    onError: () => {
      showErrorSnackbar('Unable to create client. Please try again later.')
    },
    onSuccess: () => {
      overlay.close()
      showSuccessSnackbar('Client created')
    },
  })

  const methods = useForm<FormValues>({ defaultValues })

  const {
    formState: { isValid },
    reset,
  } = methods

  useEffect(() => {
    reset(defaultValues)
  }, [overlay.isOpen, reset])

  const handleSubmit = (values: FormValues) => {
    createEntrataClient({
      cid: values.cid.trim(),
      name: values.name.trim(),
      subdomain: values.subdomain.trim(),
      product: values.product,
    })
  }

  return (
    <Dialog maxWidth="sm" {...overlay} title="New Client">
      <PixyDocsForm methods={methods} onSubmit={handleSubmit}>
        <DialogContent>
          <Stack spacing={4}>
            <Typography variant="body2" color="text.secondary">
              This will create the client&apos;s organization in Pixydocs,
              including their properties. It will begin syncing their data lists
              immediately after. Syncing may take a few minutes.
            </Typography>
            <FormTextField
              name="name"
              autoFocus
              label="Client name"
              fullWidth
              required
              useControllerProps={{ rules: requiredRule }}
            />
            <FormTextField
              name="subdomain"
              label="Client path"
              fullWidth
              helperText="Also called the subdomain."
              required
              useControllerProps={{ rules: requiredRule }}
            />
            <FormTextField
              name="cid"
              label="CID"
              fullWidth
              helperText="ex: 12345"
              required
              useControllerProps={{ rules: requiredRule }}
            />
            <FormRadioGroup name="product" label="Product type">
              {(
                Object.entries(ENTRATA_CLIENT_PRODUCT_LABELS) as [
                  EntrataClientProduct,
                  string,
                ][]
              ).map(([value, label]) => (
                <FormControlLabel
                  key={value}
                  value={value}
                  control={<Radio />}
                  label={label}
                />
              ))}
            </FormRadioGroup>
          </Stack>
        </DialogContent>

        <DialogFooter>
          <Button variant="text" onClick={overlay.close}>
            Cancel
          </Button>
          <LoadingButton type="submit" disabled={!isValid} loading={isPending}>
            Create
          </LoadingButton>
        </DialogFooter>
      </PixyDocsForm>
    </Dialog>
  )
}
